import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { SupabaseClient } from '../supabase/client';
import type {
  BarcodeLookupResult,
  BarcodeStockUpdateInput,
  BarcodeStockUpdateResult,
} from '../types/barcode';
import { findVariantByBarcode, receiveVariantStock } from '../services/barcode.service';

export const barcodeKeys = {
  all: ['barcode'] as const,
  lookup: (storeId: string, normalized: string) =>
    ['barcode', 'lookup', storeId, normalized] as const,
};

export function useBarcodeLookup(
  client: SupabaseClient,
  storeId: string | null,
): UseMutationResult<
  BarcodeLookupResult,
  unknown,
  { rawBarcode: string; requestId: number; signal?: AbortSignal }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ rawBarcode, signal }) => {
      if (!storeId) throw Object.assign(new Error('missing store id'), { code: '42501' });
      return findVariantByBarcode(client, { storeId, rawBarcode }, { signal });
    },
    onSuccess: (result) => {
      if (!storeId || result.status === 'invalid') return;
      queryClient.setQueryData(barcodeKeys.lookup(storeId, result.normalized), result);
    },
  });
}

export function useReceiveVariantStock(
  client: SupabaseClient,
): UseMutationResult<BarcodeStockUpdateResult, unknown, BarcodeStockUpdateInput> {
  return useMutation({
    mutationFn: (input) => receiveVariantStock(client, input),
  });
}

export async function invalidateBarcodeRelatedQueries(
  queryClient: QueryClient,
  args: { storeId: string; productId?: string; variantId?: string; normalized?: string },
): Promise<void> {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: ['stock-summary', args.storeId] }),
    queryClient.invalidateQueries({ queryKey: ['products', 'list', args.storeId] }),
  ];

  if (args.productId) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: ['products', 'detail', args.productId] }),
    );
  }
  if (args.normalized) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: barcodeKeys.lookup(args.storeId, args.normalized),
      }),
    );
  }

  await Promise.all(invalidations);
}
